import { Editor } from '@tiptap/react';
import { Link } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button, Input, Popover } from 'ui/components';
import { cn } from 'utils/cn';

export default function LinkInsertButton({ editor }: { editor: Editor }) {
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // defer the selection
      setTimeout(() => {
        if (editor.isActive('link')) {
          editor.chain().focus().extendMarkRange('link').run();
        }

        const { from, to } = editor.state.selection;
        const text = editor.state.doc.textBetween(from, to);
        setLinkText(text);

        const linkMark = editor.getAttributes('link');
        if (linkMark.href) {
          setLinkUrl(linkMark.href);
        } else {
          setLinkUrl('');
        }
      }, 0);
    }
  }, [isOpen, editor]);

  useEffect(() => {
    const handleUpdate = () => {
      editor.view.state.doc.descendants((node, pos) => {
        if (
          node.type.name === 'text' &&
          node.marks.find((mark) => mark.type.name === 'link')
        ) {
          const linkMark = node.marks.find((mark) => mark.type.name === 'link');
          if (linkMark) {
            const linkStart = pos;
            const linkEnd = pos + node.nodeSize;
            const linkText = node.text;

            if (linkText && linkText.trim().length === 0) {
              editor
                .chain()
                .focus()
                .setTextSelection({ from: linkStart, to: linkEnd })
                .unsetLink()
                .run();
            }
          }
        }
      });
    };

    editor.on('update', handleUpdate);

    return () => {
      editor.off('update', handleUpdate);
    };
  }, [editor]);

  const insertLink = () => {
    if (linkUrl) {
      if (editor.isActive('link')) {
        editor.chain().focus().extendMarkRange('link').run();
      }

      editor
        .chain()
        .focus()
        .setLink({ href: linkUrl })
        .run();

      if (linkText) {
        if (editor.state.selection.empty) {
          editor.chain().focus().insertContent(linkText).run();
        } else {
          editor
            .chain()
            .focus()
            .deleteSelection()
            .insertContent(linkText)
            .run();
        }
      }
    } else {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    }
    clearAndClose();
  };

  const clearAndClose = () => {
    setLinkText('');
    setLinkUrl('');
    setIsOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      clearAndClose();
    } else {
      setIsOpen(true);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            'text-gray-600',
            editor.isActive('link') && 'bg-gray-100'
          )}
        >
          <Link />
        </Button>
      </Popover.Trigger>
      <Popover.Content
        className="w-72 p-3"
        align="center"
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        <div className="flex flex-col space-y-2">
          <Input
            type="text"
            placeholder="Link text"
            value={linkText}
            onChange={(e) => setLinkText(e.target.value)}
          />
          <Input
            type="url"
            placeholder="URL"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
          />
          <div className="flex justify-between">
            <Button onClick={insertLink} size="sm" variant="muted">
              {editor.isActive('link') ? 'Update Link' : 'Insert Link'}
            </Button>
            <Button variant="outline" onClick={clearAndClose} size="sm">
              Cancel
            </Button>
          </div>
        </div>
      </Popover.Content>
    </Popover>
  );
}